import { request } from '@/renderer/utils/api';
import { cookieHasPreview } from '@/renderer/utils/previewMode';
import { IS_SERVER } from '@/renderer/utils/ssr';
import {
  SPORT_RECORD_IDS,
  type SportRecord,
  type SportRecordKey,
  since2020StatsUrl,
  sportRecordFromStats,
} from './sportRecords';

// IDs of the products shown in the services section
const NFL_PRODUCT_ID = 63;

interface SportCumulative {
  win: number;
  loss: number;
  push: number;
  pending: number;
  canceled: number;
  noAction: number;
  risk: number;
  units: number;
  total: number;
}

export interface DataType {
  nflProduct?: API.MembershipProduct;
  cfbProducts?: API.MembershipProduct[];
  isPreview?: boolean;
  stats?: Record<number, { cumulative: SportCumulative }>;
  /** Each sport's since-2020 record; a sport is missing when its request failed. */
  sportRecords?: Partial<Record<SportRecordKey, SportRecord>>;
}

const RECORD_KEYS = Object.keys(SPORT_RECORD_IDS) as SportRecordKey[];

const baseStats = {
  win: 0,
  loss: 0,
  pending: 0,
  push: 0,
  canceled: 0,
  noAction: 0,
  risk: 0,
  units: 0,
  total: 0,
};

const url =
  '/api/v1/release/stats?page[limit]=100&data[playType][]=side&data[playType][]=total&data[playType][]=prop&data[playType][]=future&data[playType][]=not-available';

export async function data(_pageContext: Vike.PageContext): Promise<DataType> {
  try {
    // Forward incoming cookies on SSR so admin session is recognised for preview mode
    const incomingHeaders =
      IS_SERVER && 'headers' in _pageContext
        ? (_pageContext.headers as Record<string, string>)
        : undefined;
    // Preview mode: URL param takes priority; cookie persists it across navigation
    const incomingCookie = incomingHeaders?.cookie;
    const isPreviewParam = _pageContext.urlParsed?.search?.preview === '1';
    const isPreviewCookie = cookieHasPreview(incomingCookie);
    const preview = isPreviewParam || isPreviewCookie ? '&preview=1' : '';
    // Availability on this page is the PUBLIC catalogue's, never the viewer's own:
    // outside preview the product requests go out signed out (no cookies, no
    // bearer), so a signed-in member, or one whose session is mid-refresh, sees the
    // same Join now as everyone else. Preview keeps the admin's session.
    // (SSR forwards no headers, so it is already signed out; Workers' fetch has no
    // `credentials`.)
    const productInit = preview
      ? { headers: incomingHeaders }
      : IS_SERVER
        ? {}
        : { credentials: 'omit' as const };
    const productOpts = preview ? { skipRefresh: true } : { skipRefresh: true, authToken: '' };
    const timeout = () =>
      typeof AbortSignal === 'undefined'
        ? {}
        : { signal: AbortSignal.timeout(IS_SERVER ? 750 : 7500) };
    // The sport cards' since-2020 records, one public, cached request per sport. A
    // failed request leaves that card without numbers rather than stale ones.
    const sportRecordsPromise = Promise.all(
      RECORD_KEYS.map((key) =>
        request<API.ReleaseStats>(
          { input: since2020StatsUrl(SPORT_RECORD_IDS[key]), init: timeout() },
          { skipRefresh: true, hideAlert: true },
        )
          .then((result) => [key, sportRecordFromStats(result)] as const)
          .catch(() => [key, undefined] as const),
      ),
    );
    const [statsResult, productsResult, cfbProductsResult, sportRecordEntries] = await Promise.all([
      // A slow or failed all-time rollup must not take the products (and so the
      // Join now link) down with it.
      request<API.ReleaseStats>({ input: url, init: timeout() }, { skipRefresh: true }).catch(
        () => null,
      ),
      // NFL product by ID
      request<API.MembershipProduct[]>(
        {
          input: `/api/v1/product?data[id]=${NFL_PRODUCT_ID}${preview}`,
          init: { ...productInit, ...timeout() },
        },
        productOpts,
      ).catch(() => null),
      // CFB products by sportId (includes FBS + FCS)
      request<API.MembershipProduct[]>(
        {
          input: `/api/v1/product?data[sportId]=1&page[limit]=10${preview}`,
          init: { ...productInit, ...timeout() },
        },
        productOpts,
      ).catch(() => null),
      sportRecordsPromise,
    ]);

    const sportRecords: DataType['sportRecords'] = {};
    for (const [key, record] of sportRecordEntries) {
      if (record) {
        sportRecords[key] = record;
      }
    }

    const stats: Record<
      API.Id,
      {
        cumulative: API.ReleaseStats['seasonId'][1];
        seasons: API.ReleaseStats['seasonId'];
        firstSeasonId: API.Id;
        lastSeasonId: API.Id;
        firstSeasonDate: number;
        lastSeasonDate: number;
        last4Ids: API.Id[];
        last4: API.ReleaseStats['seasonId'][1][];
        seasonsMeta: Record<API.Id, Partial<API.Season>>;
        sportMeta: Partial<API.Sport> | undefined;
      }
    > = {};

    if (statsResult?.seasonId) {
      for (const seasonKey in statsResult.seasonId) {
        const season = statsResult.seasonId[seasonKey];
        let sportId = season.meta.sport?.[0]?.id;
        let seasonId = season.meta.season?.[0]?.id;
        const seasonStats = season.stats;
        let sportMeta: Partial<API.Sport> | undefined = {};

        if (season.meta.sport) {
          sportMeta = Object.values(season.meta.sport)[0];
          sportId = sportMeta?.id;
        }

        if (sportId !== 1 && sportId !== 2 && sportId !== 3) {
          continue; // we only care about CFB, CBB, NFL
        }

        if (!sportId || sportId < 1) {
          continue; // skip these ones
        }

        // Initialize result set by sportId
        if (!stats[sportId]) {
          stats[sportId] = {
            cumulative: { ...baseStats },
            seasons: {},
            firstSeasonId: Infinity,
            lastSeasonId: -Infinity,
            firstSeasonDate: Infinity,
            lastSeasonDate: -Infinity,
            last4Ids: [],
            last4: [],
            seasonsMeta: {},
            sportMeta,
          };
        }

        const sportRef = stats[sportId];

        let seasonDate = 0;

        if (season.meta.season) {
          const seasonMeta = Object.values(season.meta.season)[0];

          if (seasonMeta) {
            seasonId = seasonMeta.id;

            if (!seasonId || seasonId < 1) {
              continue; // skip these ones
            }

            seasonDate = Date.parse(seasonMeta.startDate || seasonMeta.endDate);

            sportRef.seasonsMeta[seasonId] = seasonMeta;
          }
        }

        if (!seasonId || seasonId < 1) {
          continue; // skip these ones
        }

        // Initialize result set season stats inside sportId
        if (!sportRef.seasons[seasonId]) {
          sportRef.seasons[seasonId] = { ...baseStats };
        }

        const seasonRef = sportRef.seasons[seasonId];

        let islast4 = false;

        // is this one of the last 4 seasons?
        if (sportRef.last4Ids.length < 4) {
          sportRef.last4Ids.push(seasonId);

          sportRef.last4Ids.sort((aSeasonId, bSeasonId) => {
            const aSeasonDate = Date.parse(
              sportRef.seasonsMeta[aSeasonId].startDate || sportRef.seasonsMeta[aSeasonId].endDate,
            );
            const bSeasonDate = Date.parse(
              sportRef.seasonsMeta[bSeasonId].startDate || sportRef.seasonsMeta[bSeasonId].endDate,
            );
            return aSeasonDate > bSeasonDate ? 1 : aSeasonDate < bSeasonDate ? -1 : 0;
          });

          sportRef.last4.push({ ...baseStats });

          islast4 = true;
        } else if (sportRef.last4Ids[0] < seasonId) {
          sportRef.last4Ids.shift();
          sportRef.last4Ids.push(seasonId);

          sportRef.last4.shift();

          sportRef.last4.push({ ...baseStats });

          const joinedSeasons = sportRef.last4Ids
            .map((seasonId, idx) => ({ seasonId: seasonId, values: sportRef.last4[idx] }))
            .sort(({ seasonId: aSeasonId }, { seasonId: bSeasonId }) => {
              const aSeasonDate = Date.parse(
                sportRef.seasonsMeta[aSeasonId].startDate ||
                  sportRef.seasonsMeta[aSeasonId].endDate,
              );
              const bSeasonDate = Date.parse(
                sportRef.seasonsMeta[bSeasonId].startDate ||
                  sportRef.seasonsMeta[bSeasonId].endDate,
              );
              return aSeasonDate > bSeasonDate ? 1 : aSeasonDate < bSeasonDate ? -1 : 0;
            });

          sportRef.last4Ids = joinedSeasons.map((item) => item.seasonId);
          sportRef.last4 = joinedSeasons.map((item) => item.values);

          islast4 = true;
        }

        // Is this the first or last season?
        if (seasonDate > sportRef.lastSeasonDate) {
          sportRef.lastSeasonDate = seasonDate;
          sportRef.lastSeasonId = seasonId;
        }

        if (seasonDate < sportRef.firstSeasonDate) {
          sportRef.firstSeasonDate = seasonDate;
          sportRef.firstSeasonId = seasonId;
        }

        for (const result in seasonStats) {
          switch (result) {
            case 'win':
            case 'loss':
            case 'pending':
            case 'push':
            case 'noAction':
            case 'canceled': {
              const resultStats = seasonStats[result];

              seasonRef[result] += resultStats.total;
              sportRef.cumulative[result] += resultStats.total;

              seasonRef.risk += season.risk;
              seasonRef.units += season.units;
              seasonRef.total += season.total;

              if (islast4) {
                sportRef.last4[sportRef.last4.length - 1][result] += resultStats.total;

                sportRef.last4[sportRef.last4.length - 1].risk += resultStats.risk;
                sportRef.last4[sportRef.last4.length - 1].units += resultStats.units;
                sportRef.last4[sportRef.last4.length - 1].total += resultStats.total;
              }
            }
          }
        }

        sportRef.cumulative.risk += season.risk;
        sportRef.cumulative.units += season.units;
        sportRef.cumulative.total += season.total;
      }
    }

    const blocks: Record<
      API.Id,
      [
        [[API.Id], API.ReleaseStats['seasonId'][1], string],
        [API.Id[], Record<keyof API.ReleaseStats['seasonId'][2], number>, string],
      ]
    > = {};

    for (const sportId of [1, 2, 3]) {
      const sportStats = stats?.[sportId];

      const _currentSeasonId = sportStats?.last4Ids[sportStats?.last4Ids.length - 1];
      const _currentSeason = sportStats?.last4[sportStats?.last4.length - 1];
      // const lastSeasonId = sportStats?.last4Ids[sportStats?.last4Ids.length - 2];
      // const lastSeason = sportStats?.last4[sportStats?.last4.length - 2];

      const sportPrev3Ids = sportStats?.last4Ids ? sportStats.last4Ids.slice(0, 3) : [];
      const sportPrev3 = sportStats?.last4 ? sportStats.last4.slice(0, 3) : [];

      if (sportPrev3Ids.length === sportStats?.last4Ids.length) {
        // not enough seasons, remove one more
        sportPrev3Ids.length -= 1;
        sportPrev3.length -= 1;
      }

      const _sportLast3Cumulative = sportPrev3?.reduce(
        (obj, season) => {
          for (const kresult in season) {
            const result = kresult as keyof (typeof sportPrev3)[2];
            if (obj[result]) {
              obj[result] += season[result] as number;
            } else {
              obj[result] = season[result] as number;
            }
          }

          return obj;
        },
        {} as Record<keyof (typeof sportPrev3)[2], number>,
      );

      blocks[sportId] = [
        // [[currentSeasonId], currentSeason, 'current season'],
        // [[lastSeasonId], lastSeason, 'last season'],
        // [sportPrev3Ids, sportLast3Cumulative, `last ${sportPrev3Ids.length} seasons`],
        [
          [sportStats?.firstSeasonId, sportStats?.lastSeasonId],
          sportStats?.cumulative,
          '(all time)',
        ],
      ];
    }

    // API list endpoints return { data: [...], pageInfo: {...} } not a bare array
    const nflArray: API.MembershipProduct[] | null = Array.isArray(productsResult)
      ? productsResult
      : Array.isArray((productsResult as any)?.data)
        ? (productsResult as any).data
        : null;

    const nflProduct = nflArray?.find((p) => p.id === NFL_PRODUCT_ID);

    const cfbArray: API.MembershipProduct[] | null = Array.isArray(cfbProductsResult)
      ? cfbProductsResult
      : Array.isArray((cfbProductsResult as any)?.data)
        ? (cfbProductsResult as any).data
        : null;

    const cfbProducts = cfbArray ?? undefined;
    const isPreview = isPreviewParam || isPreviewCookie;

    return { stats, sports: blocks, nflProduct, cfbProducts, isPreview, sportRecords };
  } catch (error) {
    console.error(error);
  }

  return {};
}
